import React, { useState, useMemo, useRef, Ref, KeyboardEvent, ReactNode } from 'react'
import styled from 'styled-components'
import debounce from 'debounce'
import { ICustomTextFieldProps } from '../CustomTextField/CustomTextField'
import { SluglineSelectList } from './SluglineSelectList'
import { CustomTextFieldOrigin } from '../CustomTextFieldOrigin'
import { IOption } from '../../types/common'
import {
  substituteActiveSlugterm,
  pickValueToSearch,
} from '../../services/slug'

const Wrapper = styled.div`
  position: relative;
`

export interface ITypeaheadSlugline extends ICustomTextFieldProps {
  loadOptions: (value: string) => Promise<IOption[]>
  onChange: (e) => void
  onBlur?: (e) => void
  onSelect?: (e: string) => void
  defaultValueAdornment?: ReactNode
  name?: string
  label?: string
  placeholder?: string
}

export const TypeaheadSluglineOrigin = ({
  name,
  label,
  helperText,
  placeholder,
  disabled,
  error,
  value,
  loadOptions,
  onBlur,
  onChange,
  onSelect,
  defaultValueAdornment,
}: ITypeaheadSlugline) => {
  const [options, setOptions]: [IOption[], any] = useState([])
  const [showMenu, setShowMenu] = useState(false)
  const [loadingProcesses, setLoadingProcesses] = useState(0)
  const [optionIndex, setOptionIndex] = useState(0)

  const debouncedSearch = useMemo(
    () => debounce((valueToSearch) => {
      setLoadingProcesses(1)
      loadOptions(valueToSearch)
        .then(setOptions)
        .finally(() => {
          setLoadingProcesses(0)
        })
    }, 300),
    [loadOptions]
  )
  const handleInputChange = (e): void => {
    setOptionIndex(0)
    const { value: inputValue, selectionStart } = e.target
    setShowMenu(true)
    onChange(e)
    if (loadOptions && inputValue) {
      const valueToSearch = pickValueToSearch(inputValue, selectionStart)
      if (valueToSearch) {
        debouncedSearch(valueToSearch)
      } else {
        setOptions([])
        setShowMenu(false)
      }
    }
  }

  const inputField: Ref<any> = useRef(null)
  const handleSelect = (slugValue: string) => {
    if (onSelect) {
      onSelect(
        substituteActiveSlugterm(
          inputField.current.value,
          inputField.current.selectionStart,
          slugValue
        )
      )
    }
    inputField.current.focus()
    setOptions([])
    setShowMenu(false)
  }

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setOptionIndex((optionIndex + 1) % options.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setOptionIndex((options.length + optionIndex - 1) % options.length)
        break
      case 'Enter':
        e.preventDefault()
        if (options[optionIndex]) {
          handleSelect(options[optionIndex].label)
        }
        break
      case 'Tab':
        if (options[optionIndex]) {
          e.preventDefault()
          handleSelect(options[optionIndex].label)
        }
        break
      default:
    }
  }

  const handleBlur = (e): void => {
    setTimeout(() => {
      setShowMenu(false)
    }, 500)
    if (onBlur) {
      onBlur(e)
    }
  }

  return (
    <Wrapper>
      <CustomTextFieldOrigin
        id={name}
        name={name}
        label={label}
        helperText={helperText}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        onChange={handleInputChange}
        onFocus={() => {
          setShowMenu(true)
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        value={value}
        inputRef={inputField}
        defaultValueAdornment={defaultValueAdornment}
        autoComplete="off"
        aria-controls="simple-menu"
        aria-haspopup="true"
      />
      {showMenu && (
        <SluglineSelectList
          options={options}
          selected={optionIndex}
          loadingProcesses={loadingProcesses}
          handleSelect={handleSelect}
        />
      )}
    </Wrapper>
  )
}
